'use client';
import React, { useRef, useState } from 'react';
import { Tooltip, TooltipProps } from '../tooltip';

/**
 * Icon size
 * @default 'small'
 * Added 'default' size for custom size, when specific height and width is NOT needed
 */
export type IconSize =
  | '2xs'
  | 'xs'
  | 'small'
  | 'medium'
  | 'large'
  | 'x-large'
  | 'default';

export type IconProps = {
  id?: string;
  icon: string;
  iconColor?: string;
  type: 'static' | 'interactive';
  size?: IconSize;
  status?: 'normal' | 'active' | 'focus' | 'disabled' | 'selected';
  onClick?: (value: any) => void;
  needRedDot?: boolean;
  className?: string;
  noHover?: boolean;
  tooltip?: string;
  tooltipDirection?: 'top' | 'right' | 'bottom' | 'left';
  arrowLocation?: TooltipProps['arrowLocation'];
  tooltipPortal?: boolean;
  children?: React.ReactNode;
  iconStyle?: { [key: string]: string };
  'data-test-id'?: string;
};

const RedDot = () => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="6"
      height="6"
      viewBox="0 0 6 6"
      fill="none"
    >
      <circle cx="3" cy="3" r="3" fill="#DC2626" />
    </svg>
  );
};

export const Icon = ({
  ref,
  id = 'icon',
  icon,
  iconColor,
  className,
  type = 'interactive',
  size = 'small',
  status = 'normal',
  noHover,
  onClick,
  needRedDot = false,
  tooltip,
  tooltipDirection = 'bottom',
  arrowLocation = 'middle',
  tooltipPortal = false,
  iconStyle,
  children,
  'data-test-id': dataTestId,
}: IconProps & {
  ref?: React.RefObject<HTMLElement | null>;
}) => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [isHovered, setIsHovered] = useState(false);
  const wrapperSize = {
    '2xs': 'h-4 w-4',
    xs: 'h-5 w-5',
    small: 'h-5 w-5',
    medium: 'h-6 w-6',
    large: 'h-7 w-7',
    'x-large': 'h-12 w-12',
    default: '',
  };
  const selectedBg = {
    '2xs': 'h-6 w-6',
    xs: 'h-7 w-7',
    small: 'h-7 w-7',
    medium: 'h-8 w-8',
    large: 'h-9 w-9',
    'x-large': 'h-14 w-14',
    default: '',
  };

  const iconSize = {
    '2xs': 'text-sm w-3 h-3',
    xs: 'text-sm w-3 h-3',
    small: 'text-base w-5 h-5',
    medium: 'text-xl w-6 h-6',
    large: 'text-2xl w-7 h-7',
    'x-large': 'text-5xl w-12 h-12',
    default: '',
  };
  const iconStatus = {
    normal: iconColor ? iconColor : 'text-gray-900',
    active: 'text-primary-700',
    focus: 'text-primary-500',
    disabled: 'text-input-bg-disabled',
    selected: 'text-primary-700',
  };

  const noClickable = noHover || status === 'disabled';
  const iconHoverClass = noClickable ? '' : 'hover:text-primary-500';

  const iconClasses = [
    'flex! items-center justify-center',
    icon,
    iconSize[size],
    type === 'static' ? iconStatus['normal'] : iconStatus[status],
    type === 'static' ? '' : iconHoverClass,
    className ? className : '',
  ].join(' ');

  const wraapperClasses = [
    wrapperSize[size],
    noClickable ? 'cursor-default' : 'cursor-pointer',
    'group relative flex items-center justify-center',
  ].join(' ');

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (noClickable) return;
    onClick?.(e);
  };

  return (
    <div
      className={wraapperClasses}
      id={id}
      ref={wrapperRef}
      data-test-id={dataTestId}
      role="presentation"
      aria-hidden="true"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <i
        className={iconClasses}
        onClick={handleClick}
        ref={ref}
        style={iconStyle}
      />
      {status === 'selected' && (
        <div
          className={'absolute rounded-full bg-blue-100 ' + selectedBg[size]}
        />
      )}
      {needRedDot && (
        <div className="absolute -top-1 -right-1">
          <RedDot />
        </div>
      )}
      {!noClickable && tooltip && (
        <Tooltip
          targetRef={wrapperRef}
          className={
            tooltipPortal ? undefined : 'invisible group-hover:visible'
          }
          show={tooltipPortal ? isHovered : undefined}
          direction={tooltipDirection}
          arrowLocation={arrowLocation}
          portal={tooltipPortal}
        >
          {tooltip}
        </Tooltip>
      )}
      {children}
    </div>
  );
};

Icon.displayName = 'Icon';
